import React, { useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNumber } from "@/lib/format";
import { fmtDate } from "@/lib/dt";   // Batch 10 · B9 (F-82)
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Copy } from "lucide-react";
import { ReviewControls } from "@/components/reviewpoints/ReviewPointsPanel";
import { asArray } from "@/lib/asArray";
import { VouchingDuplicateBadge } from "@/components/DocRefDuplicate";   // Batch 30 · M76
import { DOC_REVIEW_ACCEPT } from "@/lib/docReviewFiles";   // Batch 33 · T7: /documents/upload takes PDF / images only
import { SupportRefTypedMark, SupportAmountHeaders, SupportAmountCells } from "@/components/VouchingSupportAmount";   // Batch 34 · B34-9 / B34-10
import { LinkedDocsCount } from "@/components/VouchingDocViewer";   // Batch 34 · B34-11: the Docs count opens the documents
import { useResizableColumns, ResizableTh, ResetCaption } from "@/components/ui/resizable-table";   // Batch 35 · B35-8
import { VouchingCreditTag, VouchingReversalTag } from "@/components/VouchingCreditTag";   // Batch 35 · P53-R2 · R56-1
import { assertionLabel, assertionTitle } from "@/components/VouchingAssertionLabel";   // PR 56 review · R56-3

/**
 * Vouching sheet table — extracted so we can (a) keep row-level assertion
 * gating (`applicable_group`) in one place, and (b) apply sticky-left columns
 * + horizontal scroll around a single scrollable region.
 */

const TX_ASSERTIONS = ["Occurrence", "Completeness (Tx)", "Accuracy", "Cut-off",
                        "Classification (Tx)", "Presentation (Tx)"];
const BAL_ASSERTIONS = ["Existence", "Rights and Obligations", "Completeness (Bal)",
                         "Accuracy, Valuation and Allocation", "Classification (Bal)",
                         "Presentation (Bal)"];


/* Batch 35 · B35-8: every column of the sheet, in display order, with its default and minimum width (px). The full list is
   stored per user (localStorage "resizable-cols:vouching") even when a column is hidden on this sheet (assertion group not
   present, no selection reason, no review column), so a width set on one sheet holds on every sheet. Minimums keep a column's
   content readable: Amount 110, Support ref 140, Account 220; text wraps, never cut. */
export const VOUCH_COLS = [
  { key: "uid", width: 80, min: 64 },
  { key: "voucher", width: 130, min: 100 },
  { key: "date", width: 104, min: 96 },
  { key: "account", width: 240, min: 220 },
  { key: "amount", width: 130, min: 110 },
  { key: "supportRef", width: 160, min: 140 },
  { key: "docAmount", width: 160, min: 130 },
  { key: "diff", width: 110, min: 96 },
  { key: "docs", width: 90, min: 72 },
  { key: "reason", width: 170, min: 120 },
  ...TX_ASSERTIONS.map((a) => ({ key: `tx:${a}`, width: 76, min: 56 })),
  ...BAL_ASSERTIONS.map((a) => ({ key: `bal:${a}`, width: 76, min: 56 })),
  { key: "copy", width: 64, min: 52 },
  { key: "overall", width: 76, min: 64 },
  { key: "bypass", width: 90, min: 72 },
  { key: "remarks", width: 200, min: 140 },
  { key: "review", width: 84, min: 64 },
];
const COL_IDX = Object.fromEntries(VOUCH_COLS.map((c, i) => [c.key, i]));
const COL_DEFAULTS = VOUCH_COLS.map((c) => c.width);
const COL_MINS = VOUCH_COLS.map((c) => c.min);
const FROZEN = ["uid", "voucher", "date", "account"];
// header cells wrap (a long title goes to a second line instead of being cut)
const TH = "px-2 py-2 align-bottom whitespace-normal break-words";

export default function VouchingTable({
  items, t, AssertionCell, copyFromPrev,
  toggleTested, toggleBypass, updateRemarks, saveRemarks, load,
  engagementId, sampleId, highlightItemId, saveSupportRef, uploadRowDoc, draft,
}) {
  // Batch 28 · Follow-up 1 · F2 (b): with ``draft`` (useTableDraft of the sheet) the row edits — tested, missing-doc bypass,
  // remarks, support reference, assertion ticks — go into the row draft and are saved with the table's Save; changed rows
  // are highlighted and a row that failed shows its error. Copy from previous, attach a document and review points stay
  // immediate row actions.
  const rowOf = (it) => (draft ? draft.rowValue(it) : it);
  // Step 11c · C3c · find 26: from the CURRENT draft, not this render's copy — two fast ticks both stay
  const setAssertion = (it) => (a, cell) => {
    const apply = (prev) => {
      const cur = { ...(prev || {}) };
      if (cell) cur[a] = cell; else delete cur[a];
      return cur;
    };
    if (draft.update) draft.update(it.id, "assertions", apply);
    else draft.edit(it.id, "assertions", apply(rowOf(it).assertions));
  };
  const { lang } = useI18n();
  // Scroll to (and briefly ring) the highlighted item when a reviewer
  // deep-links from the Review Points dashboard: /vouching?sample=…&highlight=<item_id>.
  React.useEffect(() => {
    if (!highlightItemId) return;
    const t0 = setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="vouch-row-${highlightItemId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t0);
  }, [highlightItemId]);
  // Which groups actually appear in this sheet? Drives whether the group
  // header spans render at all.
  const groupsInSheet = new Set(items.map((i) => i.applicable_group || "transaction"));
  const showTx  = groupsInSheet.has("transaction");
  const showBal = groupsInSheet.has("balance");

  const showReason = items.some((i) => i.selection_reason);
  // Column count for empty-state colspan (and the group-header spans).
  const lead = 9 + (showReason ? 1 : 0);
  const tail = 4 + (engagementId ? 1 : 0);
  const totalCols = lead + (showTx ? 6 : 0) + (showBal ? 6 : 0) + tail;

  // Batch 35 · B35-8: resizable columns (widths kept per user), the visible ones in display order
  const vouchCols = useResizableColumns({ storageKey: "vouching", defaults: COL_DEFAULTS, minWidths: COL_MINS });
  const { colWidths, onResizeStart } = vouchCols;
  const visible = VOUCH_COLS.filter((c) => (
    (c.key !== "reason" || showReason) && (!c.key.startsWith("tx:") || showTx) && (!c.key.startsWith("bal:") || showBal)
    && (c.key !== "review" || !!engagementId)));
  const w = (k) => colWidths[COL_IDX[k]];
  const tableWidth = visible.reduce((sum, c) => sum + w(c.key), 0);
  // the four frozen columns (UID, voucher, date, account) sit after the widths of the frozen ones before them
  const side = lang === "ar" ? "right" : "left";
  const stick = (k) => ({ [side]: FROZEN.slice(0, FROZEN.indexOf(k)).reduce((sum, x) => sum + w(x), 0) });
  const th = (k, cls, children, extra = {}) => (
    <ResizableTh key={k} index={COL_IDX[k]} onResizeStart={onResizeStart} className={`${TH} ${cls}`} {...extra}>{children}</ResizableTh>
  );

  return (
    <div className="overflow-x-auto" data-testid="vouching-scroll-wrap">
      <table className="text-sm" style={{ tableLayout: "fixed", width: tableWidth }} data-testid="vouching-table">
        <ResetCaption cols={vouchCols} />
        <colgroup>
          {visible.map((c) => <col key={c.key} style={{ width: w(c.key) }} data-testid={`vouch-col-${c.key}`} />)}
        </colgroup>
        <thead className="border-b bg-slate-50 text-xs text-muted-foreground">
          <tr className="border-b">
            <th colSpan={lead} className="px-2 py-1"></th>
            {showTx && (
              <th colSpan={6} className="border-x px-2 py-1 text-center uppercase tracking-wider text-[10px] text-emerald-800 bg-emerald-50 dark:bg-emerald-950/30"
                    data-testid="vouch-hdr-tx">
                {t("vouchingExtras.txAssertions")}
              </th>
            )}
            {showBal && (
              <th colSpan={6} className="border-e px-2 py-1 text-center uppercase tracking-wider text-[10px] text-fuchsia-800 bg-fuchsia-50 dark:bg-fuchsia-950/30"
                    data-testid="vouch-hdr-bal">
                {t("vouchingExtras.balAssertions")}
              </th>
            )}
            <th colSpan={tail} className="px-2 py-1"></th>
          </tr>
          <tr className="uppercase bg-slate-50">
            {th("uid", "sticky z-20 bg-slate-50 text-start", t("vouching.uid"), { style: stick("uid") })}
            {th("voucher", "sticky z-20 bg-slate-50 text-start", t("vouching.voucher"), { style: stick("voucher") })}
            {th("date", "sticky z-20 bg-slate-50 text-start", t("common.date"), { style: stick("date") })}
            {th("account", "sticky z-20 bg-slate-50 text-start border-e", t("common.account"), { style: stick("account") })}
            {th("amount", "text-end", t("common.amount"))}
            {th("supportRef", "text-start", t("vouchingExtras.supportRef"))}
            <SupportAmountHeaders t={t} onResizeStart={onResizeStart} indices={{ docAmount: COL_IDX.docAmount, diff: COL_IDX.diff }} thClass={TH} />
            {th("docs", "text-center", t("vouchingExtras.docs"))}
            {showReason && th("reason", "text-start", t("vouchingExtras.selectionReason"))}
            {showTx && TX_ASSERTIONS.map((a) => th(`tx:${a}`, "!px-1 text-center text-[10px] bg-emerald-50/40 dark:bg-emerald-950/10",
                                                     assertionLabel(a, t), { title: assertionTitle(a, t) }))}
            {showBal && BAL_ASSERTIONS.map((a) => th(`bal:${a}`, "!px-1 text-center text-[10px] bg-fuchsia-50/40 dark:bg-fuchsia-950/10",
                                                       assertionLabel(a, t), { title: assertionTitle(a, t) }))}
            {th("copy", "text-center", t("vouchingExtras.copy"), { title: t("vouchingExtras.copyAllAssertions") })}
            {th("overall", "text-center", t("vouchingExtras.overall"))}
            {th("bypass", "text-center", t("vouchingExtras.missDocBypass"), { title: t("vouching.bypassHint") })}
            {th("remarks", "text-start", t("vouchingExtras.remarks"))}
            {engagementId && th("review", "text-center text-[10px]", t("vouchingExtras.review"), { title: t("vouchingExtras.reviewOnEntry") })}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={totalCols} className="px-4 py-6 text-center text-muted-foreground">
              {t("vouchingExtras.noRows")}
            </td></tr>
          )}
          {items.map((it0, idx) => {
            const it = rowOf(it0);
            const group = it.applicable_group || "transaction";
            const rowShowsTx  = group === "transaction";
            const rowShowsBal = group === "balance";
            return (
              <tr key={it.id}
                    className={`border-b hover:bg-slate-50/60 ${(draft && draft.rowClass(it.id)) || "bg-white dark:bg-slate-950"} ${
                      highlightItemId === it.id
                        ? "outline outline-2 outline-amber-400 outline-offset-[-2px] bg-amber-50/50"
                        : ""}`}
                    data-changed={draft && draft.isChanged(it.id) ? "1" : "0"} data-failed={draft && draft.errorOf(it.id) ? "1" : "0"}
                    data-testid={`vouch-row-${it.id}`}>
                <td className="sticky z-10 bg-inherit px-2 py-2 font-mono text-[10px] text-emerald-700 break-words" style={stick("uid")}>{it.sample_item_uid || "—"}</td>
                <td className="sticky z-10 bg-inherit px-2 py-2 font-medium break-words" style={stick("voucher")}>
                  <div className="flex flex-col gap-1">
                    <span>{it.voucher_no}</span>
                    {draft && draft.errorOf(it.id) && (
                      <span className="whitespace-normal text-[10px] text-red-700" role="alert" data-testid={`vouch-row-error-${it.id}`}>{draft.errorOf(it.id)}</span>
                    )}
                    {(it.triggered_rules || []).length > 0 && (
                      <div className="flex flex-wrap gap-1"
                             data-testid={`vouch-row-triggered-${it.id}`}>
                        {asArray(it.triggered_rules).map((tr, i) => {
                          const label = tr.rule || "";
                          // Deterministic pastel per rule label — same rule always
                          // renders the same colour so scanning down a sheet is fast.
                          let h = 0;
                          for (let k = 0; k < label.length; k++) h = (h * 31 + label.charCodeAt(k)) >>> 0;
                          const hue = h % 360;
                          return (
                            <span key={i}
                                     title={tr.reason || label}
                                     className="inline-block max-w-full truncate rounded px-1.5 py-0.5 text-[9px] font-medium"
                                     style={{
                                       backgroundColor: `hsl(${hue} 90% 94%)`,
                                       color: `hsl(${hue} 60% 30%)`,
                                       border: `1px solid hsl(${hue} 55% 78%)`,
                                     }}
                                     data-testid={`vouch-row-triggered-chip-${it.id}-${i}`}>
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </td>
                <td className="sticky z-10 bg-inherit px-2 py-2 text-xs whitespace-nowrap" style={stick("date")}>{fmtDate(it.entry_date) || "—"}</td>
                <td className="sticky z-10 bg-inherit px-2 py-2 text-xs border-e whitespace-normal break-words" style={stick("account")}
                    data-testid={`vouch-account-${it.id}`}>{it.account_code} · {it.account_name}</td>
                <td className="px-2 py-2 num-cell text-xs whitespace-nowrap" data-testid={`vouch-amount-${it.id}`}>{fmtNumber(it.amount, lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<VouchingCreditTag item={it} /><VouchingReversalTag item={it} /></td>
                <td className="px-2 py-2 text-[10px] break-words">
                  {/* Batch 9 · A19: editable (saved on blur) */}
                  {draft
                    ? <input value={it.support_doc_reference || ""} onChange={(e) => draft.edit(it.id, "support_doc_reference", e.target.value)}
                             className="w-full rounded border px-1 py-0.5 text-[10px] dark:bg-slate-900" placeholder="—" data-testid={`vouch-support-ref-${it.id}`} />
                    : <SupportRefCell item={it} onSave={saveSupportRef} />}
                  <SupportRefTypedMark item={it0} t={t} />
                </td>
                <SupportAmountCells item={it0} t={t} lang={lang} onChanged={load} readOnly={!saveSupportRef && !draft} />
                <td className="px-2 py-2 text-center text-xs whitespace-nowrap">
                  <LinkedDocsCount engId={engagementId} docIds={it.document_ids} label={it.voucher_no} testId={`vouch-docs-${it.id}`} />
                  <VouchingDuplicateBadge item={it} lang={lang} />
                  {uploadRowDoc && (
                    <label className="ms-1 cursor-pointer rounded border px-1 text-[10px]" title={t("vouchingExtras.attachDoc")} data-testid={`vouch-doc-upload-${it.id}`}>
                      +<input type="file" className="hidden" accept={DOC_REVIEW_ACCEPT} onChange={(e) => { uploadRowDoc(it, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  )}
                </td>
                {showReason && (
                  <td className="px-2 py-2 text-[10px] break-words" data-testid={`vouch-reason-${it.id}`}>{it.selection_reason_label || it.selection_reason || "—"}</td>
                )}
                {showTx && TX_ASSERTIONS.map((a) => (
                  <td key={a}
                        className={`px-1 py-2 ${rowShowsTx ? "bg-emerald-50/20 dark:bg-emerald-950/5" : "bg-slate-100/60 dark:bg-slate-900/40"}`}
                        title={rowShowsTx ? "" : t("r56.naBalance")}
                        data-testid={rowShowsTx ? undefined : `disabled-cell-${it.id}-${a}`}>
                    {rowShowsTx
                      ? <AssertionCell item={it} assertion={a} onChange={load} onSet={draft ? setAssertion(it0) : undefined} />
                      : <div className="mx-auto h-5 w-5 rounded border border-dashed border-slate-300" aria-disabled="true" />}
                  </td>
                ))}
                {showBal && BAL_ASSERTIONS.map((a) => (
                  <td key={a}
                        className={`px-1 py-2 ${rowShowsBal ? "bg-fuchsia-50/20 dark:bg-fuchsia-950/5" : "bg-slate-100/60 dark:bg-slate-900/40"}`}
                        title={rowShowsBal ? "" : t("r56.naPnl")}
                        data-testid={rowShowsBal ? undefined : `disabled-cell-${it.id}-${a}`}>
                    {rowShowsBal
                      ? <AssertionCell item={it} assertion={a} onChange={load} onSet={draft ? setAssertion(it0) : undefined} />
                      : <div className="mx-auto h-5 w-5 rounded border border-dashed border-slate-300" aria-disabled="true" />}
                  </td>
                ))}
                <td className="px-2 py-2 text-center">
                  <button type="button" onClick={() => copyFromPrev(it, idx)}
                            className="rounded-md border border-slate-300 p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                            title={t("vouchingExtras.copyPrevAssertions")}
                            data-testid={`vouch-copy-${it.id}`}>
                    <Copy className="h-3 w-3" />
                  </button>
                </td>
                <td className="px-2 py-2 text-center">
                  <Checkbox checked={!!it.tested}
                              onCheckedChange={(v) => (draft ? draft.edit(it.id, "tested", !!v) : toggleTested(it, !!v))}
                              data-testid={`vouch-tested-${it.id}`} />
                </td>
                <td className="px-2 py-2 text-center" title={t("vouching.bypassHint")}>
                  <Checkbox checked={!!it.bypass_missing_doc}
                              onCheckedChange={(v) => (draft ? draft.edit(it.id, "bypass_missing_doc", !!v) : toggleBypass(it, !!v))}
                              data-testid={`vouch-bypass-${it.id}`} />
                </td>
                <td className="px-2 py-2">
                  <Input value={it.remarks || ""}
                          onChange={(e) => (draft ? draft.edit(it.id, "remarks", e.target.value) : updateRemarks(it, e.target.value))}
                          onBlur={() => { if (!draft) saveRemarks(it); }}
                          className="h-7 text-xs w-full"
                          data-testid={`vouch-remarks-${it.id}`} />
                </td>
                {engagementId && (
                  <td className="px-2 py-2 text-center">
                    <ReviewControls engagementId={engagementId}
                                     targetType="vouching_entry"
                                     targetId={it.id}
                                     vouchingSheetId={sampleId}
                                     targetLabel={`${it.voucher_no} · ${it.account_name || ""}`}
                                     compact />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// Batch 9 · A19 (F-72): the support reference is typed locally and saved on blur (the row never re-renders mid-typing)
function SupportRefCell({ item, onSave }) {
  const [v, setV] = useState(item.support_doc_reference || "");
  React.useEffect(() => { setV(item.support_doc_reference || ""); }, [item.support_doc_reference]);
  if (!onSave) return item.support_doc_reference || <span className="text-muted-foreground italic">—</span>;
  return (
    <input value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if (v !== (item.support_doc_reference || "")) onSave(item, v); }}
           onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
           className="w-full rounded border px-1 py-0.5 text-[10px] dark:bg-slate-900" placeholder="—" data-testid={`vouch-support-ref-${item.id}`} />
  );
}
